import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ApiService } from './api.service';
import { Selection } from 'src/app/domain/models/selection';

@Injectable({
    providedIn: 'root'
})
export class SelectionService {
    private readonly dynamicCache = new Map<string, Observable<Selection[]>>();

    ethnicitySelections$ = this.getSelections('Ethnicity');

    raceSelections$ = this.getSelections('Race').pipe(shareReplay());

    barriersSelections$ = this.getSelections('Barriers');

    laborForceStatusSelections$ = this.getSelections('LaborForceStatus');

    workStatusSelections$ = this.getSelections('WorkStatus');

    employmentStatusSelections$ = this.getSelections('EmploymentStatus');

    employmentStatusAltSelections$ = this.getSelections('EmploymentStatusAlt');

    employmentStatusTerseSelections$ = this.getSelections(
        'EmploymentStatusTerse'
    );

    registeredWithEmploymentAgencySelections$ = this.getSelections(
        'RegisteredWithEmploymentAgency'
    ).pipe(shareReplay());

    receivingPublicAssistanceSelections$ = this.getSelections(
        'ReceivingPublicAssistance'
    );

    wioaPartnerProgramSelections$ = this.getSelections('WioaPartnerPrograms');

    maritalStatusSelections$ = this.getSelections('MaritalStatus');

    incomePeriodSelections$ = this.getSelections('IncomePeriod');

    immigrationStatusSelections$ = this.getSelections('ImmigrationStatus');

    hasF1OrJ1VisaSelections$ = this.getSelections('HasF1OrJ1Visa');

    countryOfOriginSelections$ = this.getSelections('CountryOfOrigin');

    disabilitySelections$ = this.getSelections('Disability');

    disabilityAccommodationsSelections$ = this.getSelections(
        'DisabilityAccommodations'
    );

    disabilityDocumentedSelections$ = this.getSelections(
        'DisabilityDocumented'
    );

    referralSourceSelections$ = this.getSelections('ReferralSource');

    sexSelections$ = this.getSelections('Sex');

    sexBinarySelections$ = this.getSelections('SexBinary');

    genderIdentitySelections$ = this.getSelections('GenderIdentity');

    pronounSelections$ = this.getSelections('Pronouns');

    contactPreferenceSelections$ = this.getSelections('ContactPreference');

    stateSelections$ = this.getSelections('State');

    highestLevelOfEducationSelections$ = this.getSelections(
        'HighestLevelOfEducation'
    );

    highestLevelOfEducationAltSelections$ = this.getSelections(
        'HighestLevelOfEducationAlt'
    );

    schoolingInUSSelections$ = this.getSelections('SchoolingInUS');

    primaryLanguageSelections$ = this.getSelections('PrimaryLanguage');
    programSelections$ = this.getSelections('Programs');
    programSingleSelectSelections$ = this.getSelections('ProgramSingleSelect');

    reasonsForAttendingSelections$ = this.getSelections('ReasonsForAttending');

    hasSocialSecurityNumberSelections$ = this.getSelections(
        'HasSocialSecurityNumber'
    );

    canContactByTextSelections$ = this.getSelections('CanContactByText');

    goalsSelections$ = this.getSelections('Goals');

    applicationLocationSelections$ = this.getSelections('ApplicationLocation');

    computerEquipmentSelections$ = this.getSelections('ComputerEquipment');

    countryOfBirthSelections$ = this.getSelections('CountryOfBirth');

    citizenshipStatusSelections$ = this.getSelections('CitizenshipStatus');

    immigrantStatusSelections$ = this.getSelections('ImmigrantStatus');

    refugeeStatusSelections$ = this.getSelections('RefugeeStatus');

    motherUnder17Selections$ = this.getSelections('MotherUnder17');

    correctionsStatusSelections$ = this.getSelections('CorrectionsStatus');

    comfortUsingTechnologySelections$ = this.getSelections(
        'ComfortUsingTechnology'
    );

    childDependentCareNeededSelections$ = this.getSelections(
        'ChildDependentCareNeeded'
    );

    transportationNeededSelections$ = this.getSelections(
        'TransportationNeeded'
    );

    residenceAreaSelections$ = this.getSelections('ResidenceArea');
    desiredScheduleSelections$ = this.getSelections('DesiredSchedule');

    eligibilityStatusSelections$ = this.getSelections('EligibilityStatus');
    eligibilityCategorySelections$ = this.getSelections('EligibilityCategory');
    eligibilityConsentSelections$ = this.getSelections('EligibilityConsent');

    disabilityYesNoSelections$ = this.getSelections('DisabilityYesNo');
    disabilityAssistanceYesNoSelections$ = this.getSelections(
        'DisabilityAssistanceYesNo'
    );

    veteranSelections$ = this.getSelections('Veteran');
    militaryServiceExperienceSelections$ = this.getSelections(
        'MilitaryServiceExperience'
    );
    selectiveServiceNotificationSelections$ = this.getSelections(
        'SelectiveServiceNotification'
    );

    reasonMinorAttendingAdultEducationSelections$ = this.getSelections(
        'ReasonMinorAttendingAdultEducation'
    );
    lastSchoolAttendedSelections$ = this.getSelections(
        'LastSchoolAttendedSelect'
    );
    reasonLeftSchoolSelections$ = this.getSelections('ReasonLeftSchool');
    schoolStatusAtEntrySelections$ = this.getSelections('SchoolStatusAtEntry');

    releaseOfInformationSelections$ = this.getSelections(
        'ReleaseOfInformation'
    );

    constructor(private apiService: ApiService) {}

    getSelectionsByOptionsKey(
        optionsKey?: string | null
    ): Observable<Selection[]> {
        const normKey = optionsKey?.toLowerCase().trim();
        if (!normKey) {
            return of([]);
        }

        const selectionType = normKey;
        if (!selectionType) {
            return of([]);
        }

        if (!this.dynamicCache.has(selectionType)) {
            this.dynamicCache.set(
                selectionType,
                this.getSelections(selectionType).pipe(shareReplay(1))
            );
        }

        return this.dynamicCache.get(selectionType)!;
    }

    private getSelections(selectionType: string): Observable<Selection[]> {
        return this.apiService
            .get(
                `/clients/${runtimeConfig.clientId.toString()}/selections/${selectionType}`
            )
            .pipe(shareReplay());
    }
}
